import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UiIconComponent } from '@seatix/shared/ui/ui-icons';
import { interval, map, take } from 'rxjs';

@Component({
  selector: 'stx-deployment-refresh-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    UiIconComponent,
  ],
  host: {
    class: 'flex flex-col items-center gap-6 p-6',
  },
  template: `
    <h2
      mat-dialog-title
      class="text-center"
    >
      ¡Nueva versión de la app disponible!
    </h2>

    <mat-dialog-content>
      <div class="flex min-w-80 flex-col place-items-center gap-4">
        <div class="bg-primary/30 rounded-xl p-2">
          <stx-ui-icon
            size="64"
            name="refresh"
            class="text-primary"
          />
        </div>

        <p class="text-center text-lg">
          Refrescando página en <strong>{{ countdown() }}</strong
          >...
        </p>

        <mat-progress-bar
          mode="determinate"
          class="w-full"
          [value]="progress()"
        />
      </div>
    </mat-dialog-content>
  `,
})
export class DeploymentRefreshDialogComponent {
  readonly #TICK_INTERVAL_MS = 100;
  readonly #TOTAL_DURATION_MS = 3000;
  readonly #totalTicks = this.#TOTAL_DURATION_MS / this.#TICK_INTERVAL_MS;
  readonly #dialogRef = inject(MatDialogRef<DeploymentRefreshDialogComponent>);

  readonly #tick = toSignal(
    interval(this.#TICK_INTERVAL_MS).pipe(
      take(this.#totalTicks + 1),
      map((tick) => tick + 1),
    ),
    { initialValue: 0 },
  );

  protected readonly progress = computed(() => {
    const tick = this.#tick();
    return 100 - Math.min((tick / this.#totalTicks) * 100, 100);
  });

  protected readonly countdown = computed(() => {
    const tick = this.#tick();
    const remainingMs = this.#TOTAL_DURATION_MS - tick * this.#TICK_INTERVAL_MS;
    const remainingSeconds = Math.round(remainingMs / 1000);
    return Math.max(remainingSeconds, 0);
  });

  constructor() {
    effect(() => {
      if (this.#tick() > this.#totalTicks) {
        this.#dialogRef.close(true);
      }
    });
  }
}
